import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { Loader } from '../../../../../atoms';
import { currency } from '../../../utils';
import { getStripeClientSecret } from '../../../../../api/fees';

type Props = {
  onCancel?: () => void;
  // Base invoice amount (sum of selected invoice balances), before any card fee.
  amount: number;
  permitId: number | string;
  invoiceNumber: string;
  email: string;
};

// EA-1344: mirror the epic-link processing-fee model. Stripe's fee is
// card-country dependent (US: 2.9% + $0.30, international: 4.4% + $0.30). We
// gross up so the client covers the fee and the office still nets the full
// invoice amount: totalCharge = (amount + 0.30) / (1 - rate).
type FeeBreakdown = {
  processingFee: number;
  totalCharge: number;
  isInternational: boolean;
};

const calculateFee = (invoiceAmount: number, cardCountry: string): FeeBreakdown => {
  const isInternational = cardCountry !== 'US';
  const rate = isInternational ? 0.044 : 0.029;
  const totalCharge = (invoiceAmount + 0.3) / (1 - rate);
  const processingFee = totalCharge - invoiceAmount;
  return {
    processingFee: Number(processingFee.toFixed(2)),
    totalCharge: Number(totalCharge.toFixed(2)),
    isInternational,
  };
};

const cardElementOptions = {
  hidePostalCode: false,
  style: {
    base: {
      fontSize: '16px',
      color: '#1a2238',
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
      fontSmoothing: 'antialiased',
      '::placeholder': { color: '#9aa3b2' },
    },
    invalid: { color: '#e0423f', iconColor: '#e0423f' },
  },
};

const CheckoutForm: FC<Props> = ({ onCancel, amount, permitId, invoiceNumber, email }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [cardComplete, setCardComplete] = useState<boolean>(false);
  const [feeBreakdown, setFeeBreakdown] = useState<FeeBreakdown | null>(null);
  const stripe = useStripe();
  const elements = useElements();

  const handleCardChange = async (event: any) => {
    setError('');

    if (event.error) {
      setError(event.error.message);
    }

    if (!event.complete) {
      setCardComplete(false);
      setFeeBreakdown(null);
      return;
    }

    setCardComplete(true);

    // Detect the card country so the correct processing fee (domestic vs
    // international) is shown before the client confirms.
    if (!stripe || !elements) return;
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (pmError) {
      setError(pmError.message || 'Could not read card details.');
      setFeeBreakdown(null);
      return;
    }

    if (paymentMethod?.card?.country) {
      setFeeBreakdown(calculateFee(amount, paymentMethod.card.country));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements || !feeBreakdown) return;
    setLoading(true);
    setError('');

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card details not loaded. Please refresh and try again.');
      setLoading(false);
      return;
    }

    try {
      // Create the PaymentIntent for the grossed-up total (fee included), so the
      // amount charged matches the breakdown shown to the client.
      const intentResult: any = await getStripeClientSecret({
        permit_id: permitId,
        amount: feeBreakdown.totalCharge,
        email,
        invoice_number: invoiceNumber,
      });
      const clientSecret = intentResult?.data?.clientSecret;
      if (!clientSecret) {
        setError('Could not start the payment. Please try again.');
        setLoading(false);
        return;
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
        return_url: `${window.location.origin}/client/invoices`,
      });

      if (result.error) {
        setError(result.error.message || 'An error occurred. Please try again.');
        setLoading(false);
        return;
      }

      if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        localStorage.setItem('paymentIntentId', result.paymentIntent.id);
        const url = new URL(`${window.location.origin}/client/invoices`);
        url.searchParams.set('redirect_status', 'succeeded');
        url.searchParams.set('payment_intent', result.paymentIntent.id);
        url.searchParams.set('payment_intent_client_secret', clientSecret);
        window.location.href = url.toString();
        return;
      }

      setLoading(false);
    } catch (err: any) {
      // Backstop: backend returns 503 { error: "qbo_unavailable", message }
      // when QBO is down. No PaymentIntent was created, so nothing was charged.
      if (err?.response?.status === 503) {
        setError(
          err?.response?.data?.message ||
            'Payments are temporarily unavailable. Please try again in a little while.',
        );
      } else {
        setError('Something went wrong. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FieldLabel>Card details</FieldLabel>
      <CardField $invalid={!!error}>
        <CardElement options={cardElementOptions} onChange={handleCardChange} />
      </CardField>

      <CardBrandsRow aria-hidden>
        <Brand>VISA</Brand>
        <Brand>Mastercard</Brand>
        <Brand>AMEX</Brand>
        <Brand>Discover</Brand>
      </CardBrandsRow>

      {feeBreakdown && (
        <FeeBreakdownBox>
          <FeeRow>
            <span className="label">Invoice Amount</span>
            <span className="value">{currency(amount)}</span>
          </FeeRow>
          <FeeRow>
            <span className="label">
              Processing Fee {feeBreakdown.isInternational ? '(International 4.4% + $0.30)' : '(2.9% + $0.30)'}
            </span>
            <span className="value">{currency(feeBreakdown.processingFee)}</span>
          </FeeRow>
          <FeeRowTotal>
            <span className="label">Total to be charged</span>
            <span className="value">{currency(feeBreakdown.totalCharge)}</span>
          </FeeRowTotal>
        </FeeBreakdownBox>
      )}

      {error && (
        <ErrorCallout role="alert">
          <ErrorIcon aria-hidden>!</ErrorIcon>
          <span>{error}</span>
        </ErrorCallout>
      )}

      <Footer>
        {onCancel && (
          <CancelBtn type="button" onClick={onCancel} disabled={loading}>
            Cancel
          </CancelBtn>
        )}
        <PayBtn type="submit" disabled={!stripe || !elements || loading || !cardComplete || !feeBreakdown}>
          {loading ? (
            <LoaderRow>
              <Loader />
              <span>Processing…</span>
            </LoaderRow>
          ) : feeBreakdown ? (
            <>Pay {currency(feeBreakdown.totalCharge)}</>
          ) : (
            <>Enter card details</>
          )}
        </PayBtn>
      </Footer>
    </Form>
  );
};

export default CheckoutForm;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1 1 auto;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const CardField = styled.div<{ $invalid?: boolean }>`
  padding: 12px 14px;
  border: 1px solid ${({ $invalid }) => ($invalid ? 'var(--danger-500, #e0423f)' : 'var(--border)')};
  border-radius: 8px;
  background: var(--neutral-0);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const CardBrandsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: -4px;
`;

const Brand = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--neutral-0);
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--neutral-600);
`;

const FeeBreakdownBox = styled.div`
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 14px;
`;

const FeeRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 5px 0;
  font-size: 0.875rem;

  .label {
    color: var(--neutral-600);
  }
  .value {
    font-weight: 500;
    color: var(--mainText);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const FeeRowTotal = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  border-top: 2px solid var(--border);
  margin-top: 6px;
  padding-top: 8px;

  .label {
    font-weight: 700;
    color: var(--mainText);
    font-size: 0.9375rem;
  }
  .value {
    font-weight: 700;
    color: var(--success-700, #196a3b);
    font-size: 1rem;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

const ErrorCallout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  background: #fdecec;
  border: 1px solid #f9c9c9;
  border-radius: 8px;
  color: #9a2523;
  font-size: 0.8125rem;
  line-height: 1.45;
`;

const ErrorIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--danger-500, #e0423f);
  color: var(--neutral-0);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 6px;
  margin-top: auto;
  flex-wrap: wrap;

  @media only screen and (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 8px;
  }
`;

const buttonBase = `
  height: 44px;
  min-width: 130px;
  padding: 0 22px;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.9375rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const PayBtn = styled.button`
  ${buttonBase}
  border: none;
  background: var(--primary-600);
  color: var(--neutral-0);
  flex: 1 1 auto;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  @media only screen and (min-width: 481px) {
    flex: 0 0 auto;
    min-width: 180px;
  }
`;

const CancelBtn = styled.button`
  ${buttonBase}
  background: var(--neutral-0);
  color: var(--neutral-700);
  border: 1px solid var(--border);

  &:hover:not(:disabled) {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }
`;

const LoaderRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;
